import { useEffect, useState } from 'react';

import { AppIcon, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { OwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { OWNERSHIP_DISPLAY } from './OwnershipCard';

const STRINGS = appStrings.modules.survey.output.blueprintSection.ownership;

type OwnershipLevelsModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  ownershipLevel: OwnershipLevel;
}>;

export function OwnershipLevelsModal({ open, onClose, ownershipLevel }: OwnershipLevelsModalProps) {
  const [expandedLevel, setExpandedLevel] = useState<OwnershipLevel | null>(ownershipLevel);

  useEffect(() => {
    if (open) {
      setExpandedLevel(ownershipLevel);
    }
  }, [open, ownershipLevel]);

  const handleToggle = (level: OwnershipLevel) => {
    setExpandedLevel((prev) => (prev === level ? null : level));
  };

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="sm"
      icon="AnnotationInfo"
      hideCircleImage
      headerContent={<Typography variant="display-xs-semibold">{STRINGS.modalTitle}</Typography>}
      bodyContent={
        <Box sx={listSx}>
          {OWNERSHIP_DISPLAY.map(({ level, label, icon }) => {
            const isExpanded = expandedLevel === level;
            const tagline = (STRINGS.detail.taglines as Record<OwnershipLevel, string>)[level];

            return (
              <Box
                key={level}
                id={`ownership-modal-row-${level.toLowerCase().replace(/ /g, '-')}`}
                sx={rowSx(isExpanded)}
                onClick={() => handleToggle(level)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle(level);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
              >
                <Box sx={rowHeaderSx}>
                  <AppIcon iconName={icon} diameter="20px" color="currentColor" />
                  <Typography variant="text-md-medium">{label}</Typography>
                </Box>
                <Box sx={descriptionContainerSx(isExpanded)}>
                  <Typography variant="text-sm-regular" sx={descriptionTextSx}>
                    {tagline}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      }
    />
  );
}

// --- Styles ---

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const rowSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-md'],
    padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors[isExpanded ? 'Brand/500' : 'Colors/Border/border-secondary']}`,
    cursor: 'pointer',
  });

const rowHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const descriptionContainerSx =
  (isExpanded: boolean): SxProps<Theme> =>
  () => ({
    display: 'grid',
    gridTemplateRows: isExpanded ? '1fr' : '0fr',
    opacity: isExpanded ? 1 : 0,
    transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out',
    '& > *': {
      overflow: 'hidden',
    },
  });

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
